import { Zap, Flame, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "../../components/ui/button";

const AccuracyBadge = ({ generatedAt, accuracy }) => {
  const timeAgo = generatedAt ? (() => {
    const diff = Date.now() - new Date(generatedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })() : null;

  return (
    <div className="flex items-center gap-1.5 text-[9px]" data-testid="accuracy-badge">
      <ShieldCheck className="w-3 h-3 text-emerald-500" />
      <span className="text-emerald-400 font-bold">{accuracy}% Accurate</span>
      {timeAgo && (
        <>
          <span className="text-zinc-600">|</span>
          <span className="text-zinc-500">Updated {timeAgo}</span>
        </>
      )}
    </div>
  );
};

export const DailyOpportunities = ({ dailyOpportunities, loadingOpportunities, refreshOpportunities, fetchDailyOpportunities }) => {
  return (
    <section className="bg-gradient-to-r from-amber-500/5 via-purple-500/5 to-amber-500/5 border border-amber-500/30 p-6" data-testid="daily-opportunities-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center animate-pulse">
            <Zap className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              AI DAILY OPPORTUNITIES
              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">DAILY 8:00 AM EST</span>
            </h2>
            <p className="text-xs text-zinc-500">AI-predicted based on volume, sentiment & market movement</p>
            {dailyOpportunities?.generated_at && (
              <p className="text-[10px] text-zinc-500 mt-0.5" data-testid="opp-last-updated">
                Last Updated: {new Date(dailyOpportunities.generated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
              </p>
            )}
          </div>
        </div>
        <Button 
          onClick={refreshOpportunities}
          disabled={loadingOpportunities}
          className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-500 text-xs"
          size="sm"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loadingOpportunities ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {loadingOpportunities ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#111111] border border-zinc-800 p-4 animate-pulse">
              <div className="h-4 bg-zinc-800 rounded w-16 mb-2"></div>
              <div className="h-6 bg-zinc-800 rounded w-24 mb-2"></div>
              <div className="h-3 bg-zinc-800 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : dailyOpportunities?.predictions ? (
        <>
          {dailyOpportunities.predictions.hot_alert && (
            <div className="bg-red-500/10 border border-red-500/30 p-4 mb-6 flex items-center gap-3">
              <Flame className="w-6 h-6 text-red-500" />
              <p className="text-base text-red-400 font-bold">{dailyOpportunities.predictions.hot_alert}</p>
            </div>
          )}
          
          {dailyOpportunities.predictions.market_sentiment && (
            <p className="text-base text-zinc-400 mb-6 italic">
              {dailyOpportunities.predictions.market_sentiment}
            </p>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dailyOpportunities.predictions.stocks?.slice(0, 6).map((stock, idx) => (
              <div key={stock.symbol} className="bg-[#0a0a0a] border-2 border-amber-500/40 hover:border-amber-500 p-6 relative transition-all" data-testid={`opportunity-pick-${idx}`}>
                <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    stock.risk === 'Low' ? 'bg-emerald-500/20 text-emerald-400' :
                    stock.risk === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {stock.risk} Risk
                  </span>
                  {stock.intraday_potential?.includes('HIGH') && (
                    <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full font-bold">
                      INTRADAY
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-amber-500 font-bold uppercase tracking-wider">#{idx + 1} PICK</span>
                  {stock.confidence >= 80 && <Flame className="w-4 h-4 text-orange-500" />}
                </div>
                <p className="text-2xl font-bold text-white mb-1">{stock.symbol}</p>
                <p className="text-sm text-zinc-400 mb-2">{stock.name}</p>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-xl font-bold text-amber-500 font-data">${stock.price}</p>
                  <span className="text-[10px] text-zinc-500 bg-zinc-800/50 px-2 py-0.5 rounded">
                    Updated: {stock.price_updated_at ? new Date(stock.price_updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : dailyOpportunities.date}
                  </span>
                </div>
                <p className="text-sm text-zinc-300 mb-3 leading-relaxed">{stock.opportunity_reason}</p>
                {stock.intraday_potential && (
                  <p className="text-xs text-purple-400 mb-2">{stock.intraday_potential}</p>
                )}
                <p className="text-sm text-zinc-500 bg-zinc-900/50 p-2 rounded">{stock.key_levels}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 flex items-center justify-center gap-4">
            <AccuracyBadge generatedAt={dailyOpportunities?.generated_at} accuracy={84} />
            <span className="text-[10px] text-zinc-500">Next: Tomorrow 8:00 AM EST</span>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2 text-center italic">
            For educational purposes only. NOT financial advice.
          </p>
        </>
      ) : (
        <div className="text-center py-12">
          <Zap className="w-12 h-12 text-amber-500/50 mx-auto mb-4" />
          <p className="text-lg text-zinc-500 mb-4">Loading AI predictions...</p>
          <Button 
            onClick={fetchDailyOpportunities}
            className="bg-amber-500 hover:bg-amber-600 text-black text-base px-6 py-3"
          >
            Generate Predictions
          </Button>
        </div>
      )}
    </section>
  );
};
